import React, { useState } from "react";
import { 
  Building, 
  User, 
  CalendarDays, 
  MessageSquare, 
  Cloud, 
  BookOpen, 
  Stamp, 
  Coins, 
  Gavel, 
  FileSpreadsheet, 
  Leaf, 
  Headphones, 
  ChevronDown, 
  ChevronRight,
  Compass,
  Lock,
  Receipt,
  Users,
  CalendarCheck,
  CheckCircle2,
  FolderOpen,
  ShieldCheck,
  Landmark,
  Sparkles,
  Crown,
  Cake,
  QrCode
} from "lucide-react";
import { formatLojaName } from "../utils/lodgeName";

export const SisMacomSidebar = ({
  activeModule,
  setActiveModule,
  currentUser,
  currentLoja,
  onOpenLoginModal,
  onOpenMyStatement,
  onResetData
}) => {
  // Collapsible section states matching SisMaçom
  const [openSections, setOpenSections] = useState({
    menuLoja: false,
    secretaria: false,
    chancelaria: false,
    tesouraria: false,
    presidencia: false,
    relatorios: false,
    sismacom: false,
    suporte: false
  });

  const toggleSection = (section) => {
    setOpenSections((prev) => Object.fromEntries(
      Object.keys(prev).map((key) => [key, key === section ? !prev[section] : false])
    ));
  };

  // RBAC — cada perfil enxerga somente a sua pasta. Somente Master da Loja tem tudo.
  const role = currentUser?.role || "irmao";
  const rolesArr = Array.isArray(currentUser?.roles) ? currentUser.roles.map((r) => String(r).toLowerCase()) : [];
  const hasProfile = (name) => rolesArr.includes(name.toLowerCase());

  const isMaster = currentUser?.is_master_loja || role === "master_loja" || role === "super_admin" || hasProfile("Master da Loja");
  const isVeneravel = role === "veneravel" || hasProfile("Venerável Mestre") || hasProfile("Veneravel Mestre");

  const canSee = {
    menuLoja: true, // Todos veem
    secretaria: isMaster || role === "secretaria" || hasProfile("Secretaria"),
    chancelaria: isMaster || role === "chanceler" || hasProfile("Chancelaria"),
    tesouraria: isMaster || role === "tesoureiro" || hasProfile("Tesouraria"),
    presidencia: isMaster || isVeneravel,
    relatorios: isMaster
      || role === "tesoureiro" || role === "chanceler" || role === "secretaria" || isVeneravel
      || hasProfile("Tesouraria") || hasProfile("Chancelaria") || hasProfile("Secretaria"),
  };

  const navItemClass = (itemKey) => `
    w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg text-xs font-medium transition-all text-left
    ${activeModule === itemKey 
      ? "bg-[#D4AF37]/20 text-[#D4AF37] border-l-4 border-[#D4AF37] font-bold shadow-sm" 
      : "text-slate-300 hover:text-white hover:bg-[#1E293B]/60"}
  `;

  return (
    <aside className="w-64 bg-[#0B111E] border-r border-[#D4AF37]/20 flex flex-col flex-shrink-0 min-h-screen text-slate-300 select-none">
      
      {/* Top Brand — SEMPRE mostra o brand da plataforma (Lojas Integradas) */}
      <div className="h-20 px-5 border-b border-[#D4AF37]/20 flex items-center justify-between bg-[#080D18]">
        <div className="flex items-center space-x-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#8B7513] flex items-center justify-center text-slate-950 shadow-[0_0_15px_rgba(212,175,55,0.3)] shrink-0">
            <Compass className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center space-x-1.5">
              <span className="font-serif font-bold text-[15px] text-white tracking-wide">
                Lojas<span className="text-[#D4AF37]"> Integradas</span>
              </span>
            </div>
            <p className="text-[9px] text-slate-500 tracking-widest uppercase">Plataforma SaaS Maçônica</p>
          </div>
        </div>
      </div>

      {/* Card da Loja atual — mostra o brasão específico DESTA Loja */}
      {currentLoja && (
        <div className="px-4 py-3 border-b border-slate-800 bg-[#0d1420] flex items-center gap-3">
          {currentLoja.logo_b64 ? (
            <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow shrink-0 overflow-hidden">
              <img
                src={currentLoja.logo_b64}
                alt="Brasão da Loja"
                className="w-full h-full object-contain p-0.5"
                data-testid="sidebar-loja-logo"
              />
            </div>
          ) : (
            <div className="w-11 h-11 rounded-xl bg-slate-800 flex items-center justify-center text-slate-500 shrink-0" data-testid="sidebar-loja-nologo">
              <Landmark className="w-5 h-5" />
            </div>
          )}
          <div className="min-w-0">
            <p
              className="text-[11px] text-white font-serif font-semibold truncate leading-tight"
              data-testid="sidebar-loja-name"
              title={currentLoja.name}
            >
              {formatLojaName(currentLoja, { withArls: true })}
            </p>
            {currentLoja.oriente && (
              <p className="text-[9px] text-[#D4AF37] tracking-widest uppercase">
                Or∴ {currentLoja.oriente}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Navigation Groups List */}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-1.5 scrollbar-thin">
        
        {/* 1. MENU DA LOJA */}
        <div>
          <button
            data-testid="menu-group-loja"
            onClick={() => toggleSection("menuLoja")}
            className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-white rounded-lg hover:bg-white/5"
          >
            <div className="flex items-center space-x-2 text-[#38BDF8]">
              <Building className="w-4 h-4 text-[#38BDF8]" />
              <span className="text-white">Menu da Loja</span>
            </div>
            {openSections.menuLoja ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>

          {openSections.menuLoja && (
            <div className="pl-2 pr-1 space-y-0.5 mt-1 border-l border-slate-800 ml-3">
              <button
                data-testid="nav-sub-perfil-loja"
                onClick={() => setActiveModule("perfil_loja")}
                className={navItemClass("perfil_loja")}
              >
                <Building className="w-3.5 h-3.5 text-[#38BDF8]" />
                <span>Perfil da Loja</span>
              </button>

              <button
                data-testid="nav-sub-meu-perfil"
                onClick={() => setActiveModule("meu_perfil")}
                className={navItemClass("meu_perfil")}
              >
                <User className="w-3.5 h-3.5 text-[#38BDF8]" />
                <span>Meu Perfil & Extrato</span>
              </button>

              <button
                data-testid="nav-sub-agenda"
                onClick={() => setActiveModule("agenda")}
                className={navItemClass("agenda")}
              >
                <CalendarDays className="w-3.5 h-3.5 text-[#38BDF8]" />
                <span>Agenda da Loja</span>
              </button>

              <button
                data-testid="nav-sub-mensagens"
                onClick={() => setActiveModule("mensagens")}
                className={navItemClass("mensagens")}
              >
                <MessageSquare className="w-3.5 h-3.5 text-[#38BDF8]" />
                <span>Mensagens & Avisos</span>
              </button>

              <button
                data-testid="nav-sub-arquivos"
                onClick={() => setActiveModule("arquivos")}
                className={navItemClass("arquivos")}
              >
                <Cloud className="w-3.5 h-3.5 text-[#38BDF8]" />
                <span>Arquivos (nuvem)</span>
              </button>
            </div>
          )}
        </div>

        {/* 2. SECRETARIA */}
        {canSee.secretaria && (
        <div>
          <button
            data-testid="menu-group-secretaria"
            onClick={() => toggleSection("secretaria")}
            className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-white rounded-lg hover:bg-white/5"
          >
            <div className="flex items-center space-x-2 text-amber-400">
              <BookOpen className="w-4 h-4 text-amber-400" />
              <span className="text-white">Secretaria</span>
            </div>
            {openSections.secretaria ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>

          {openSections.secretaria && (
            <div className="pl-2 pr-1 space-y-0.5 mt-1 border-l border-slate-800 ml-3">
              <button
                data-testid="nav-sub-quadro-obreiros"
                onClick={() => setActiveModule("obreiros")}
                className={navItemClass("obreiros")}
              >
                <Users className="w-3.5 h-3.5 text-amber-400" />
                <span>Membros da Loja</span>
              </button>

              <button
                data-testid="nav-sub-candidatos"
                onClick={() => setActiveModule("candidatos")}
                className={navItemClass("candidatos")}
              >
                <User className="w-3.5 h-3.5 text-amber-400" />
                <span>Candidatos</span>
              </button>

              <button
                data-testid="nav-sub-sessoes-secretaria"
                onClick={() => setActiveModule("frequencia")}
                className={navItemClass("frequencia")}
              >
                <CalendarDays className="w-3.5 h-3.5 text-amber-400" />
                <span>Sessões</span>
              </button>

              <button
                data-testid="nav-sub-balaustres"
                onClick={() => setActiveModule("balaustres")}
                className={navItemClass("balaustres")}
              >
                <BookOpen className="w-3.5 h-3.5 text-amber-400" />
                <span>Balaústres</span>
              </button>

              {isMaster && (
                <button
                  data-testid="nav-sub-usuarios"
                  onClick={() => setActiveModule("usuarios")}
                  className={navItemClass("usuarios")}
                >
                  <Lock className="w-3.5 h-3.5 text-amber-400" />
                  <span>Usuários</span>
                </button>
              )}

              <button
                data-testid="nav-sub-instrucao"
                onClick={() => setActiveModule("instrucao")}
                className={navItemClass("instrucao")}
              >
                <BookOpen className="w-3.5 h-3.5 text-amber-400" />
                <span>Câmaras de Instrução</span>
              </button>
            </div>
          )}
        </div>
        )}

        {/* 3. CHANCELARIA */}
        {canSee.chancelaria && (
        <div>
          <button
            data-testid="menu-group-chancelaria"
            onClick={() => toggleSection("chancelaria")}
            className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-white rounded-lg hover:bg-white/5"
          >
            <div className="flex items-center space-x-2 text-indigo-400">
              <Stamp className="w-4 h-4 text-indigo-400" />
              <span className="text-white">Chancelaria</span>
            </div>
            {openSections.chancelaria ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>

          {openSections.chancelaria && (
            <div className="pl-2 pr-1 space-y-0.5 mt-1 border-l border-slate-800 ml-3">
              <button
                data-testid="nav-sub-frequencia"
                onClick={() => setActiveModule("frequencia")}
                className={navItemClass("frequencia")}
              >
                <CalendarCheck className="w-3.5 h-3.5 text-indigo-400" />
                <span>Livro de Presenças</span>
              </button>
              <button
                data-testid="nav-sub-relatorio-frequencia"
                onClick={() => setActiveModule("relatorio_frequencia")}
                className={navItemClass("relatorio_frequencia")}
              >
                <FileSpreadsheet className="w-3.5 h-3.5 text-indigo-300" />
                <span>Relatório de Frequência</span>
              </button>
              <button
                data-testid="nav-sub-balaustres-chancelaria"
                onClick={() => setActiveModule("balaustres")}
                className={navItemClass("balaustres")}
              >
                <BookOpen className="w-3.5 h-3.5 text-indigo-400" />
                <span>Balaústres (Atas)</span>
              </button>
              <button
                data-testid="nav-sub-templates-balaustre"
                onClick={() => setActiveModule("templates_balaustre")}
                className={navItemClass("templates_balaustre")}
              >
                <BookOpen className="w-3.5 h-3.5 text-indigo-400" />
                <span>Modelos de Balaústres</span>
              </button>
              <button
                data-testid="nav-sub-datas-comemorativas"
                onClick={() => setActiveModule("datas_comemorativas")}
                className={navItemClass("datas_comemorativas")}
              >
                <Cake className="w-3.5 h-3.5 text-indigo-400" />
                <span>Datas Comemorativas</span>
              </button>
              <button
                data-testid="nav-sub-presenca-qr"
                onClick={() => setActiveModule("presenca_qr")}
                className={navItemClass("presenca_qr")}
              >
                <QrCode className="w-3.5 h-3.5 text-indigo-400" />
                <span>Presença por QR</span>
              </button>
            </div>
          )}
        </div>
        )}

        {/* 4. TESOURARIA */}
        {canSee.tesouraria && (
        <div>
          <button
            data-testid="menu-group-tesouraria"
            onClick={() => toggleSection("tesouraria")}
            className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-white rounded-lg hover:bg-white/5"
          >
            <div className="flex items-center space-x-2 text-emerald-400">
              <Coins className="w-4 h-4 text-emerald-400" />
              <span className="text-white">Tesouraria</span>
            </div>
            {openSections.tesouraria ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>

          {openSections.tesouraria && (
            <div className="pl-2 pr-1 space-y-0.5 mt-1 border-l border-slate-800 ml-3">
              <button
                data-testid="nav-sub-tesouraria-geral"
                onClick={() => setActiveModule("tesouraria")}
                className={navItemClass("tesouraria")}
              >
                <Coins className="w-3.5 h-3.5 text-emerald-400" />
                <span>Conta Corrente & Tronco</span>
              </button>

              <button
                data-testid="nav-sub-centros-custo"
                onClick={() => setActiveModule("centros_custo")}
                className={navItemClass("centros_custo")}
              >
                <Building className="w-3.5 h-3.5 text-emerald-400" />
                <span>Centros de Custo</span>
              </button>

              <button
                data-testid="nav-sub-mensalidades-desconto"
                onClick={() => setActiveModule("mensalidades_desconto")}
                className={navItemClass("mensalidades_desconto")}
              >
                <Receipt className="w-3.5 h-3.5 text-emerald-400" />
                <span>Capitações & Descontos</span>
              </button>

              <button
                data-testid="nav-sub-contatos"
                onClick={() => setActiveModule("contatos")}
                className={navItemClass("contatos")}
              >
                <Building className="w-3.5 h-3.5 text-emerald-400" />
                <span>Clientes & Fornecedores</span>
              </button>

              <button
                data-testid="nav-sub-relatorio-cc"
                onClick={() => setActiveModule("relatorio_cc")}
                className={navItemClass("relatorio_cc")}
              >
                <Receipt className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span>Relatório por CC</span>
              </button>
              <button
                data-testid="nav-sub-balanco-consolidado"
                onClick={() => setActiveModule("relatorios_balancete")}
                className={navItemClass("relatorios_balancete")}
              >
                <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-300" />
                <span>Balanço Consolidado</span>
              </button>
            </div>
          )}
        </div>
        )}

        {/* 5. PRESIDÊNCIA */}
        {canSee.presidencia && (
        <div>
          <button
            data-testid="menu-group-presidencia"
            onClick={() => toggleSection("presidencia")}
            className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-white rounded-lg hover:bg-white/5"
          >
            <div className="flex items-center space-x-2 text-[#D4AF37]">
              <Gavel className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-white">Master da Loja</span>
            </div>
            {openSections.presidencia ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>

          {openSections.presidencia && (
            <div className="pl-2 pr-1 space-y-0.5 mt-1 border-l border-slate-800 ml-3">
              <button
                data-testid="nav-sub-dashboard-presidencia"
                onClick={() => setActiveModule("dashboard")}
                className={navItemClass("dashboard")}
              >
                <Compass className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span>Painel Executivo da Loja</span>
              </button>
              <button
                data-testid="nav-sub-auditoria"
                onClick={() => setActiveModule("auditoria")}
                className={navItemClass("auditoria")}
              >
                <ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span>Trilha de Auditoria</span>
              </button>
              <button
                data-testid="nav-sub-grand-orient"
                onClick={() => setActiveModule("grand_orient")}
                className={navItemClass("grand_orient")}
              >
                <Crown className="w-3.5 h-3.5 text-purple-300" />
                <span>Dashboard Executivo (Ouro)</span>
              </button>
              <button
                data-testid="nav-sub-loja-settings"
                onClick={() => setActiveModule("loja_settings")}
                className={navItemClass("loja_settings")}
              >
                <Landmark className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span>Papel Timbrado & Loja</span>
              </button>
              <button
                data-testid="nav-sub-diretoria-gestao"
                onClick={() => setActiveModule("diretoria_gestao")}
                className={navItemClass("diretoria_gestao")}
              >
                <Crown className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span>Diretoria da Gestão</span>
              </button>
              <a
                data-testid="nav-sub-pricing"
                href="/pricing"
                className={navItemClass("__pricing__") + " flex items-center space-x-2"}
              >
                <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span>Planos & Assinatura</span>
              </a>
            </div>
          )}
        </div>
        )}

        {/* SISMAÇOM & SUPORTE */}
        <div className="pt-2 border-t border-slate-800/80">
          <button
            data-testid="btn-sidebar-switch-login"
            onClick={onOpenLoginModal}
            className="w-full flex items-center space-x-3 px-3.5 py-2 rounded-lg text-xs font-semibold text-slate-300 hover:text-[#D4AF37] hover:bg-[#1E293B]/60 transition-all text-left"
          >
            <Lock className="w-4 h-4 text-[#D4AF37]" />
            <span>Trocar Perfil / Senhas</span>
          </button>
        </div>

      </div>

      {/* User Footer Profile */}
      <div className="p-3.5 bg-[#080D18] border-t border-[#D4AF37]/20 flex items-center justify-between">
        <div className="flex items-center space-x-2.5 overflow-hidden">
          <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] font-bold text-xs shrink-0">
            {currentUser?.name?.charAt(3) || "V"}
          </div>
          <div className="overflow-hidden">
            <span className="text-xs font-bold text-white block truncate leading-tight">
              {currentUser?.name || "—"}
            </span>
            <span className="text-[10px] text-[#D4AF37] block truncate">
              {currentUser?.office || currentUser?.role || "—"}
            </span>
          </div>
        </div>
      </div>

    </aside>
  );
};
